import React from "react";
import wdImage from "../../assets/services_web-development-img.jpg";
import madImage from "../../assets/services_mobile-app-development-img.jpg";
import uiImage from "../../assets/services_ui-design-img.jpg";
import { Icon } from "@iconify/react";
import { useLandingContent } from "../../lib/hooks/useContent";

type ServiceCardProps = {
  image?: string;
  icon?: string;
  title?: string;
  description?: string;
  bgColor?: string;
};

function ServiceCard({ image, icon, title, description, bgColor }: ServiceCardProps) {
  return (
    <a
      href="/pricing"
      className={`${
        bgColor ?? "bg-[#1a3c8a]"
      } rounded-2xl shadow-lg w-80 min-h-[400px] overflow-hidden transform transition duration-300 hover:scale-105 hover:shadow-xl`}
    >
      {/* Image */}
      <div className="h-40 w-full flex items-center justify-center">
        {image ? (
          <img src={image} alt={title || "Service"} className="w-full h-40 object-cover rounded-lg" />
        ) : (
          <div className="w-full h-40 bg-[#2c3e70] rounded-lg" />
        )}
      </div>

      <div className="p-6 text-center flex flex-col justify-between h-[calc(100%-224px)] space-y-3">
        {/* Icon */}
        <span className="flex items-center justify-center text-5xl mb-2">
          <Icon icon={icon || "mdi:star-outline"} />
        </span>

        {/* Title */}
        <h3 className="mt-2 text-xl font-semibold text-white">{title || "Untitled service"}</h3>

        {/* Description */}
        <p className="mt-2 text-sm text-[#DEE3F2]">{description || "Details coming soon."}</p>

      </div>
    </a>
  );
}

export default function Services() {
  const { data, loading, errors } = useLandingContent() as any;

  const services = (data?.services ?? []) as Array<{
    id: string;
    title?: string;
    description?: string;
    image_url?: string; // <- snake_case from API
    icon?: string;
    position?: number;
    is_featured?: boolean;
    bgColor?: string; // optional (not from API)
  }>;

  // Only treat Services failure as blocking for this section
  if (loading && services.length === 0) {
    return (
      <section className="py-16 bg-[#080D1B] text-center">
        <h2 className="text-3xl font-bold text-[#DEE3F2] mb-10">Our Services</h2>
        <p className="text-[#DEE3F2]">Loading services...</p>
      </section>
    );
  }

  if (errors?.services && services.length === 0) {
    return (
      <section className="py-16 bg-[#080D1B] text-center">
        <h2 className="text-3xl font-bold text-[#DEE3F2] mb-10">Our Services</h2>
        <p className="text-red-400">Failed to load services.</p>
      </section>
    );
  }

  // Fallback images if DB doesn't provide one
  const fallbacks = [wdImage, madImage, uiImage];

  return (
    <section id="services" className="py-16 bg-[#080D1B] text-center flex flex-col items-center">
      <div className="max-w-6xl w-full px-6 md:px-8 mx-auto text-center">
       <div className="flex flex-col items-center justify-center mb-5 ">
      <h2 className="text-5xl font-bold text-[#DEE3F2] mb-2">
        Our <span className="text-[#2B61DE]">Services</span>
        </h2>
       {/* Link */}
        <p className="mb-4 text-[#B1BFE4] font-medium">
          Click to see pricing!
        </p>
        </div>
        
      <div className="grid grid-cols-1 md:grid-cols-3 gap-24 justify-items-center">
        {services.map((s, idx) => { 
          const isEven = idx % 2 === 0 ;
          return(
          <ServiceCard
            key={s.id ?? idx}
            image={s.image_url || fallbacks[idx % fallbacks.length]}
            icon={s.icon}
            title={s.title}
            description={s.description}
            bgColor={
              isEven
              ? s.bgColor
            : "bg-[#96ADE4]"}
          />
        );
})}
        </div>
      </div>
      
    </section>
  );
}
